import React, { createContext, useEffect, useState } from "react";
import { events } from "aws-amplify/data";
import { fetchAuthSession } from "aws-amplify/auth";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

export const NotificationsContext = createContext({});

export function NotificationsProvider({ userId, children }) {
    const qc = useQueryClient();
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [error, setError] = useState(null);

    // Initialize authentication and connection
    useEffect(() => {
        let subscription;

        const setupNotifications = async () => {
            try {
                // Ensure we have valid authentication
                const session = await fetchAuthSession({ forceRefresh: true });

                if (!session.credentials) {
                    throw new Error("No AWS credentials available");
                }

                setConnectionStatus('authenticated');

                // Connect to user-specific notification channel
                const channel = await events.connect(`/messages/user/${userId}`);

                subscription = channel.subscribe({
                    next: (msg) => {

                        // Invalidate inbox queries to refresh notification data
                        qc.invalidateQueries({ queryKey: ["inbox"] });
                        qc.invalidateQueries({ queryKey: ["inbox", "unread_count"] });

                        // Show toast notification if message has a subject
                        if (msg?.subject) {
                            const notificationType = msg.notification_type || 'info';

                            // Create toast based on notification type
                            const toastOptions = {
                                position: "top-right",
                                autoClose: 5000,
                                hideProgressBar: false,
                                closeOnClick: true,
                                pauseOnHover: true,
                                draggable: true,
                            };

                            switch (notificationType) {
                                case 'success':
                                    toast.success(msg.subject, toastOptions);
                                    break;
                                case 'warning':
                                    toast.warning(msg.subject, toastOptions);
                                    break;
                                case 'error':
                                    toast.error(msg.subject, toastOptions);
                                    break;
                                default:
                                    toast.info(msg.subject, toastOptions);
                                    break;
                            }
                        }
                    },
                    error: (err) => {
                        console.error("❌ Notification subscription error:", err);
                        setError(err.message);
                        setConnectionStatus('subscription_failed');
                    },
                });

                setConnectionStatus('connected');

            } catch (err) {
                console.error("❌ Notification setup failed:", err);
                setError(err.message);
                setConnectionStatus('setup_failed');
            }
        };

        setupNotifications();

        // Cleanup subscription on unmount
        return () => {
            if (subscription) {
                subscription.unsubscribe();
            }
        };
    }, [userId, qc]);

    return (
        <NotificationsContext.Provider value={{
            connectionStatus,
            error,
            isConnected: connectionStatus === 'connected'
        }}>
            {children}
        </NotificationsContext.Provider>
    );
}